import { FunctionComponent } from "react";

import { LeadLabelType } from "@signalist/shared-types";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useLazyGetLeadRelationshipQuery } from "@/pages/Private/redux/list/list-relationship.api";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { resolveLeadReviewTarget } from "@/pages/Private/pages/List/utils/resolveLeadReviewTarget";

import { LEAD_LABEL_COLOR, SEGMENT_COLORS } from "./LeadColorLabels";
import { LeadRelationshipPopover } from "./LeadRelationshipPopover";

/**
 * The lead's color dots (PREC-3547). Each dot is its own hover trigger that opens the consolidated
 * relationship popover with that dot's section flashed. The full popover payload is fetched on
 * demand (cached per lead), so the listing endpoints stay light.
 */
export const LeadColorDots: FunctionComponent<{
	row: ListPerson;
	isMoreThanOne?: boolean;
	/** Lead-card surfaces pass this to select the lead + open its chat tab; tables omit it (href used). */
	onReviewChat?: () => void;
}> = ({ row, isMoreThanOne = false, onReviewChat }) => {
	const labels = row.labels ?? [];
	const [trigger, { data: response, isFetching, isError }] = useLazyGetLeadRelationshipQuery();
	const data = response?.data;

	if (labels.length <= 1 && !isMoreThanOne) {
		return null;
	}

	const reviewChatHref = onReviewChat ? undefined : resolveLeadReviewTarget(row);

	const fetchRelationship = () => {
		void trigger({ listPersonId: row.id }, true);
	};

	return (
		<div className="flex flex-col gap-[3px] items-center justify-center">
			{labels.map((labelType: LeadLabelType) => (
				<Tooltip
					key={labelType}
					panel
					direction="bottom"
					content={
						<LeadRelationshipPopover
							data={data}
							isLoading={isFetching && !data}
							isError={isError}
							highlightColor={LEAD_LABEL_COLOR[labelType]}
							reviewChatHref={reviewChatHref}
							onReviewChat={onReviewChat}
						/>
					}
				>
					<div
						onMouseEnter={fetchRelationship}
						onFocus={fetchRelationship}
						className={`w-2 h-2 rounded-full shrink-0 cursor-pointer ${SEGMENT_COLORS[LEAD_LABEL_COLOR[labelType]]}`}
					/>
				</Tooltip>
			))}
		</div>
	);
};
